import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from '../config';
import './SearchBar.css';

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [pendingQuery, setPendingQuery] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatLoading]);

  // Send pending query once modal is open and messages are reset
  useEffect(() => {
    if (pendingQuery && chatModalOpen && chatMessages.length === 0 && !chatLoading) {
      const q = pendingQuery;
      setPendingQuery(null);
      sendChatMessage(q);
    }
  }, [pendingQuery, chatModalOpen, chatMessages, chatLoading]);

  // Send a message to the /api/chat endpoint
  const sendChatMessage = async (text) => {
    const content = (text || chatInput || '').trim();
    if (!content || chatLoading) return;

    const userMessage = { role: 'user', content };
    const updatedMessages = [...chatMessages, userMessage];
    setChatMessages(updatedMessages);
    setChatInput('');
    setChatLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updatedMessages }),
      });
      const data = await response.json();
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.reply || 'Sorry, I could not generate a response.' },
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Handle search bar submit — open chat modal with the query
  const handleSubmit = () => {
    const q = query.trim();
    if (!q) return;
    setChatMessages([]);
    setChatModalOpen(true);
    setQuery('');
    setPendingQuery(q);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const clearSearch = () => {
    setQuery('');
  };

  // Render modal via portal so it escapes any overflow:hidden parent
  const chatModal = chatModalOpen
    ? ReactDOM.createPortal(
        <div className="search-bar-container">
          <div className="search-modal-overlay search-chat-overlay" onClick={() => setChatModalOpen(false)}>
            <div className="search-modal search-chat-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header search-chat-header">
                <h2 className="modal-title">AI Assistant</h2>
                <button type="button" className="modal-close" onClick={() => setChatModalOpen(false)} aria-label="Close">✕</button>
              </div>
              <div className="search-chat-content">
                <div className="search-chat-messages">
                  {chatMessages.length === 0 && !chatLoading && (
                    <div className="search-chat-placeholder">
                      Ask about your portfolio: properties, tenants, rent, maintenance, or anything else.
                    </div>
                  )}
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`search-chat-bubble ${msg.role}`}>
                      <div className="search-chat-bubble-inner">{msg.content}</div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="search-chat-bubble assistant">
                      <div className="search-chat-bubble-inner search-chat-typing">Thinking...</div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>
                <div className="search-chat-input-row">
                  <input
                    type="text"
                    className="search-chat-input"
                    placeholder="Ask a follow-up question..."
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                    disabled={chatLoading}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="search-chat-send"
                    onClick={() => sendChatMessage()}
                    disabled={!chatInput.trim() || chatLoading}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="search-bar-container">
      <div className="search-input-wrapper">
        <button
          onClick={handleSubmit}
          className="search-button search-icon-left"
          type="button"
          disabled={!query.trim()}
        >
          🔍
        </button>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search properties, tenants, maintenance, finances..."
          className="search-input"
        />
        <div className="search-actions">
          {query && (
            <button onClick={clearSearch} className="clear-button" type="button">
              ✕
            </button>
          )}
        </div>
      </div>
      {chatModal}
    </div>
  );
};

export default SearchBar;
